import {
  Button,
  DatePicker,
  useBreadcrumbRoutes,
  Wrapper,
  Radio,
  FormTitle,
  defaultErrorHandling,
} from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import { Space, Spin } from 'antd';
import type { RadioChangeEvent } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DisplayType, getComponentStatus, TimeType } from '@/pages/EnergyBill/const';
import styles from './index.module.scss';
import useEchartsTheme from '@/shared/hooks/useEchartsTheme';
import ReactEcharts from 'echarts-for-react';
import {
  apiV2CustomerBillBillStatisticsPost,
  V2CustomerBillBillStatisticsPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { getEchartsOption, getMediumEchartsOption } from '@/pages/EnergyBill/service';
import BillTable from './components/BillTable';
import qs from 'qs';
import tittleDecoration from './tittleDecoration.png';
interface Props {}

const EnergyBillStatistics: React.FC<Props> = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const echartsTheme = useEchartsTheme();

  const [timeType, setTimeType] = useState<TimeType>(TimeType.month);
  const [displayType, setDisplayType] = useState<DisplayType>(DisplayType.chart);
  const [chartData, setChartData] = useState<V2CustomerBillBillStatisticsPostResponse>();
  const [loading, setLoading] = useState(false);

  // 时间选择器的默认值
  const getDefaultDateRange = (type: TimeType): [Dayjs, Dayjs] => {
    const now = dayjs();
    if (type === TimeType.month) {
      // 按月：默认12个月
      return [now.subtract(11, 'month').startOf('month'), now.endOf('month')];
    } else {
      // 按年：默认3年
      return [now.subtract(2, 'year').startOf('year'), now.endOf('year')];
    }
  };

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getDefaultDateRange(TimeType.month));
  const { isMonth, isChart, isTable } = getComponentStatus({ timeType, displayType });

  // 禁用不可选的日期（只禁用未来时间）
  const disabledDate = (current: Dayjs) => {
    if (!current) return false;

    const now = dayjs();
    const endOfCurrent = isMonth ? now.endOf('month') : now.endOf('year');

    // 不能选择未来
    return current > endOfCurrent;
  };

  // 处理时间范围变化
  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;

    // 确保结束时间是月末/年末的最后一天
    const endOfPeriod = isMonth ? dates[1].endOf('month') : dates[1].endOf('year');
    const newRange: [Dayjs, Dayjs] = [dates[0].startOf(isMonth ? 'month' : 'year'), endOfPeriod];
    setDateRange(newRange);
  };

  // 获取图表数据
  const fetchChartData = async (range?: [Dayjs, Dayjs]) => {
    const targetRange = range || dateRange;
    setLoading(true);
    apiV2CustomerBillBillStatisticsPost({
      fromTime: targetRange[0].valueOf(),
      toTime: targetRange[1].valueOf(),
      timeResolution: timeType,
    })
      .onError(e => {
        // 重新获取最近12个月/3年的数据
        setDateRange(getDefaultDateRange(timeType));
        defaultErrorHandling(e);
        return true;
      })
      .then(res => {
        setChartData(res);
      })
      .finally(() => setLoading(false));
  };

  // 切换时间类型时重置时间范围
  const handleTimeTypeChange = (e: RadioChangeEvent) => {
    const type = e.target.value;
    setTimeType(type);
    setDateRange(getDefaultDateRange(type));
  };

  const handleDisplayTypeChange = (e: RadioChangeEvent) => {
    setDisplayType(e.target.value);
  };

  const handleExport = () => {
    window.open(
      `/api/v2/customer/bill/billExport?` +
        qs.stringify({
          fromTime: dateRange[0].valueOf(),
          toTime: dateRange[1].valueOf(),
          timeResolution: timeType,
        })
    );
  };

  // 生成主图表配置
  const chartOption = useMemo(() => {
    return getEchartsOption({ chartData, timeType });
  }, [chartData, timeType]);

  // 为每个介质生成独立的用量图表配置
  const mediumChartOptions = useMemo(() => {
    return getMediumEchartsOption({ chartData, timeType });
  }, [chartData, timeType]);

  useEffect(() => {
    fetchChartData();
  }, [timeType, dateRange]);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <FormTitle title={'能源账单统计'} />
      <div className={styles.top}>
        <Space size={8}>
          <Radio.Group value={timeType} onChange={handleTimeTypeChange} buttonStyle="solid">
            <Radio.Button value={TimeType.month}>按月</Radio.Button>
            <Radio.Button value={TimeType.year}>按年</Radio.Button>
          </Radio.Group>
          <DatePicker.RangePicker
            picker={timeType.toLowerCase() as 'month' | 'year'}
            value={dateRange}
            onChange={handleDateRangeChange}
            disabledDate={disabledDate}
          />
          <Button type="primary" wrapStyle={{ marginLeft: 8 }} onClick={handleExport}>
            导出
          </Button>
        </Space>
        <Space size={8}>
          <Radio.Group value={displayType} onChange={handleDisplayTypeChange} buttonStyle="solid">
            <Radio.Button value={DisplayType.chart}>图表</Radio.Button>
            <Radio.Button value={DisplayType.table}>表格</Radio.Button>
          </Radio.Group>
        </Space>
      </div>
      <Spin spinning={loading}>
        {isChart && (
          <>
            <div className={styles.headerWrapper}>
              <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
              <span className={styles.title}>账单金额统计</span>
            </div>
            <ReactEcharts
              theme={echartsTheme}
              option={chartOption}
              style={{ minHeight: 320, marginTop: 16 }}
              notMerge
              lazyUpdate={false}
            />
            {/* 遍历每个介质展示用量图表 */}
            {mediumChartOptions.map(({ mediumName, option }) => (
              <>
                <div className={styles.headerWrapper}>
                  <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
                  <span className={styles.title}>用量统计-{mediumName}</span>
                </div>
                <ReactEcharts
                  key={mediumName}
                  theme={echartsTheme}
                  option={option}
                  style={{ minHeight: 320, marginTop: 24 }}
                  notMerge
                  lazyUpdate={false}
                />
              </>
            ))}
          </>
        )}
        {isTable && <BillTable chartData={chartData} />}
      </Spin>
    </Wrapper>
  );
};

export default EnergyBillStatistics;
